import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { Button, Logo, OtpInput, TextField } from '../../components/ui';
import { requestCode, verifyCode } from '../../api/auth.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { PRIVACY_POLICY_URL } from '../../config.js';

/**
 * Email one-time-code sign-in. Wired to the live endpoints.
 *
 * Two things about this backend shape the UI:
 *   - /auth/request always returns the same generic success, so the copy says
 *     "if that address is registered" rather than claiming an email was sent.
 *   - /auth/verify signals a bad code with HTTP 200 and {ok: false}, so the
 *     result is checked rather than relying on a thrown error.
 */
/**
 * "By continuing you agree to our {terms} and {privacy}."
 *
 * Split on the placeholders rather than concatenating fragments, so translators
 * can put the links anywhere the sentence needs them.
 */
function TermsNotice() {
  const { t } = useI18n();
  const parts = t('login_terms').split(/(\{terms\}|\{privacy\})/);

  return parts.map((part, i) => {
    if (part === '{terms}') {
      return (
        <Link key={i} className="link" to="/terms">
          {t('login_terms_link')}
        </Link>
      );
    }
    if (part === '{privacy}') {
      // Was an <a> to the old WordPress page. Now an in-app route, so it uses
      // Link like the terms link beside it — no full page reload mid-signup.
      return (
        <Link key={i} className="link" to={PRIVACY_POLICY_URL}>
          {t('login_privacy_link')}
        </Link>
      );
    }
    return part;
  });
}

export function Login() {
  const { t } = useI18n();
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  // Guards against a double submit when onComplete and the button both fire.
  const submitting = useRef(false);

  useEffect(() => {
    document.title = `${t('login_title')} — Don Quack`;
  }, [t]);

  // Already signed in (e.g. opened /app/login directly in a second tab).
  if (status === 'signed-in') {
    return <Navigate to={location.state?.from || '/app'} replace />;
  }

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function handleSendCode(e) {
    e?.preventDefault();
    setError('');
    setNotice('');

    if (!emailLooksValid) {
      setError(t('login_invalid_email'));
      return;
    }

    setBusy(true);
    try {
      await requestCode(email.trim().toLowerCase());
      setStep('code');
    } catch (err) {
      setError(err.message || t('error_generic'));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(codeValue) {
    const value = (codeValue ?? code).trim();
    setError('');

    if (value.length !== 6) {
      setError(t('login_code_incomplete'));
      return;
    }
    if (submitting.current) return;

    submitting.current = true;
    setBusy(true);
    try {
      const res = await verifyCode(email.trim().toLowerCase(), value);

      // A wrong code is a 200 with ok:false — not an exception.
      if (!res.ok || !res.token) {
        setError(t('login_code_invalid'));
        setCode('');
        return;
      }

      signIn(res.token, res.user);
      navigate(location.state?.from || '/app', { replace: true });
    } catch (err) {
      setError(err.message || t('error_generic'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  async function handleResend() {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await requestCode(email.trim().toLowerCase());
      setNotice(t('login_resent'));
      setCode('');
    } catch (err) {
      setError(err.message || t('error_generic'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <Logo size={30} />
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendCode} noValidate>
            <h1 className={styles.title}>{t('login_title')}</h1>
            <p className={styles.sub}>{t('login_subtitle')}</p>

            <TextField
              label={t('login_email_label')}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder={t('login_email_placeholder')}
              value={email}
              error={error || undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
            />

            <Button type="submit" block loading={busy}>
              {busy ? t('login_sending') : t('login_send_code')}
            </Button>

            <p className={styles.foot}>
              <TermsNotice />
            </p>
          </form>
        ) : (
          <div>
            <h1 className={styles.title}>{t('login_check_email')}</h1>
            <p className={styles.sub}>{t('login_code_sent_to', { email: email.trim() })}</p>

            <div className={styles.otpWrap}>
              <OtpInput
                value={code}
                onChange={(v) => {
                  setCode(v);
                  if (error) setError('');
                }}
                onComplete={handleVerify}
                disabled={busy}
                invalid={Boolean(error)}
                autoFocus
              />
            </div>

            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            {notice && !error && <p className={styles.notice}>{notice}</p>}

            <Button block loading={busy} onClick={() => handleVerify()}>
              {busy ? t('login_verifying') : t('login_verify')}
            </Button>

            <p className={styles.foot}>
              {t('login_no_code')}{' '}
              <button type="button" className={styles.linkBtn} onClick={handleResend} disabled={busy}>
                {t('login_resend')}
              </button>
              {' · '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                  setNotice('');
                }}
              >
                {t('login_change_email')}
              </button>
            </p>
          </div>
        )}
      </div>

      <Link className={styles.back} to="/">
        ← {t('login_back_to_site')}
      </Link>
    </div>
  );
}
